import React, { useEffect, useState } from 'react';
import { publicApi, Demonstration, Feedback } from '../services/api';
import { useAuth } from '../context/AuthContext';
import './FeedbackThread.css';

interface FeedbackThreadProps {
  demonstration: Demonstration;
  /** Called after a reply posts (e.g. so a queue can mark it answered). */
  onPosted?: (fb: Feedback, asInstructor: boolean) => void;
}

function timeAgo(iso: string): string {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  const m = s / 60;
  if (m < 60) return `${Math.floor(m)}m ago`;
  const h = m / 60;
  if (h < 24) return `${Math.floor(h)}h ago`;
  const d = h / 24;
  if (d < 7) return `${Math.floor(d)}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * Feedback on a demonstration, oldest first, with a reply box when the viewer
 * may post. Mirrors the backend rules (routes/feedback.ts): staff reply as
 * instructors whenever feedback was requested and is open; dancers may reply
 * on "all"-audience requests, and always on their own demo (as a note).
 */
const FeedbackThread: React.FC<FeedbackThreadProps> = ({ demonstration, onPosted }) => {
  const { user } = useAuth();
  const [items, setItems] = useState<Feedback[]>(demonstration.feedback ?? []);
  const [text, setText] = useState('');
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    publicApi.feedback
      .getByDemonstration(String(demonstration.id))
      .then((fb) => { if (!cancelled) setItems(fb); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [demonstration.id]);

  const isStaff = (user?.role === 'owner' || user?.role === 'instructor') && user?.instructorId != null;
  const isOwnDemo = user?.dancerId != null && String(user.dancerId) === String(demonstration.dancerId);
  const open = demonstration.feedbackOpen && demonstration.feedbackAudience != null;
  const canPostAsInstructor = isStaff && open;
  const canPostAsDancer =
    !canPostAsInstructor && user?.dancerId != null && (isOwnDemo || (open && demonstration.feedbackAudience === 'all'));

  const sorted = [...items].sort((a, b) => +new Date(a.createdAt) - +new Date(b.createdAt));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const content = text.trim();
    if (!content) return;
    setPosting(true);
    setError(null);
    try {
      const data = { demonstrationId: String(demonstration.id), content };
      const fb = canPostAsInstructor
        ? await publicApi.feedback.addFromInstructor(data)
        : await publicApi.feedback.addFromDancer(data);
      setItems((prev) => [...prev, fb]);
      setText('');
      onPosted?.(fb, canPostAsInstructor);
    } catch {
      setError('Couldn’t post that — try again.');
    } finally {
      setPosting(false);
    }
  };

  return (
    <section className="fb-thread" aria-label="Feedback">
      <h4 className="fb-thread-title">
        Feedback {sorted.length > 0 && <span className="fb-thread-count">{sorted.length}</span>}
      </h4>

      {sorted.length === 0 ? (
        <p className="fb-thread-empty">
          {open ? 'No feedback yet.' : 'Feedback wasn’t requested for this demonstration.'}
        </p>
      ) : (
        <ol className="fb-thread-list">
          {sorted.map((fb) => {
            const author = fb.instructor?.name || fb.dancer?.name || 'Someone';
            return (
              <li key={fb.id} className={`fb-item ${fb.instructor ? 'fb-item--coach' : ''}`}>
                <span className="fb-avatar" aria-hidden="true">{author.charAt(0)}</span>
                <div className="fb-body">
                  <div className="fb-meta">
                    <strong>{author}</strong>
                    {fb.instructor && <span className="fb-coach-tag">Instructor</span>}
                    <time dateTime={fb.createdAt}>{timeAgo(fb.createdAt)}</time>
                  </div>
                  <p>{fb.content}</p>
                </div>
              </li>
            );
          })}
        </ol>
      )}

      {(canPostAsInstructor || canPostAsDancer) && (
        <form className="fb-compose" onSubmit={submit}>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit(e);
            }}
            placeholder={
              canPostAsInstructor
                ? 'One concrete correction works best — e.g. “Wait for 5 before you turn.”'
                : isOwnDemo ? 'Add a note for yourself…' : 'Cheer them on…'
            }
            aria-label={canPostAsInstructor ? 'Reply as instructor' : 'Add a comment'}
            rows={3}
            disabled={posting}
          />
          <div className="fb-compose-row">
            {error && <span className="fb-error" role="alert">{error}</span>}
            <button type="submit" className="btn btn-primary btn-small" disabled={posting || !text.trim()}>
              {posting ? 'Posting…' : canPostAsInstructor ? 'Send feedback' : 'Post'}
            </button>
          </div>
        </form>
      )}
    </section>
  );
};

export default FeedbackThread;
